import React from 'react';
import { Box, Divider, Paper, Typography, Tooltip, FormHelperText } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { i18n } from '../../../translate/i18n';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/types';
import * as actions from '../../../store/actions';
import InfoIcon from '@mui/icons-material/Info';

type TServiceDataProps = {
  label: string,
  value: string|boolean,
  showInfo?: boolean, 
}

type Props = {
  editMode: boolean,
}
type Source =
  | "web"
  | "legacy app"
  | "legacy web"
  | "legacy"
  | "app qrcode"
  | "app calendar"
  | "app places"
  | "app allequipments"
  | "app map"
  | "app";

export const serviceSources = {
  web: i18n.t('editService.web'),
  "legacy app": i18n.t('editService.legacyApp'),
  "legacy web": i18n.t('editService.legacyWeb'),
  legacy: i18n.t('editService.legacy'),
  "app qrcode": i18n.t('editService.appQrcode'),
  "app calendar": i18n.t('editService.appCalendar'),
  "app places": i18n.t('editService.appPlaces'),
  "app allequipments": i18n.t('editService.appAllEquipments'),
  "app map": i18n.t('editService.appMap'),
  app: i18n.t('editService.app'),
}

const ServiceDetailsCard: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const selectedService = useSelector((state: RootState) => state.editService.selectedService);
  const serviceDate = useSelector((state: RootState) => state.editService.date);
  const serviceExpirationDate = useSelector((state: RootState) => state.editService.expirationDate);
  const selectedAccess = useSelector((state: any) => state.auth.selectedAccess);
  const days = moment(serviceExpirationDate).startOf('day').diff(moment().startOf('day'), 'days');
  const dayOrDaysRemaining = days === 1 ? i18n.t('editService.dayRemaining') : i18n.t('editService.daysRemaining');
  const dayOrDaysOverdue = days === -1 ? i18n.t('editService.dayOverdue') : i18n.t('editService.daysOverdue');
  const daysRemainingOrOverdueString = days >= 0 ? ` (${days} ${dayOrDaysRemaining})` : ` (${days && -1 * days} ${dayOrDaysOverdue})`;
  const isAdmin = selectedAccess.role === 'admin';
  const showCreatedAt = selectedAccess.showServiceReportCoordinates;

  const handleChangeDate = (date: Date|null) => {
    date && dispatch(actions.editServiceChangeServiceDate(date));
  }
  
  const totalActionsText = `${selectedService.totalActions}/${selectedService.completedActions} ${selectedService.completedActions === 1 ? i18n.t('editService.actionCompleted'): i18n.t('editService.actionsCompleted')}`;

  const ServiceData = (props: TServiceDataProps) => {
    const isBoolean = typeof props.value === 'boolean';
    let value = props.value;
    if (isBoolean) {
      if (props.value) value = i18n.t('editService.yes') as string;
      else value = i18n.t('editService.no') as string;
    }

    return(
      <Box>
        <Box sx={{display: 'flex'}}>
          <Typography sx={sxs.label}>
            {props.label}
          </Typography>
          {props.showInfo && <Tooltip title={i18n.t('editService.actionsMandatory') as string}>
            <InfoIcon sx={{fontSize: '12px', marginLeft: '5px' }} />
          </Tooltip>}
        </Box>
        <Typography sx={sxs.value}>
          {value}
        </Typography>
      </Box>
    )
  }

  return(
    <Paper sx={sxs.serviceDetailsCardContainer}>
      <Typography variant='h6'>
        {`${selectedService.equipmentService.serviceType.name} ${selectedService.equipmentService.frequency.name}`}
      </Typography>
      <Divider />
      <Box>
        {props.editMode && isAdmin ?
          <DatePicker
            label={i18n.t('editService.serviceDate') as string}
            value={serviceDate}
            disabled={!props.editMode}
            onChange={handleChangeDate}
            format="dd/MM/yyyy"
            maxDate={new Date(selectedService.date)}
            slotProps={{
              textField: {
                variant: 'standard',
              },
            }}
            sx={sxs.datePicker}
          />
          :
          <ServiceData label={i18n.t('editService.serviceDate')} value={serviceDate.toLocaleDateString('pt-BR')} />
        }
        {!selectedService.equipmentService.frequency.onDemand &&
          <FormHelperText>
            { `${i18n.t('editService.expiration')} ${serviceExpirationDate?.toLocaleDateString('pt-BR')} ${daysRemainingOrOverdueString}`}
          </FormHelperText>
        }
      </Box>
      {showCreatedAt && <ServiceData label={i18n.t('editService.createdAt')} value={ selectedService.createdAt ? moment(selectedService.createdAt).format('DD/MM/yyyy HH:mm') : '-'}/>}
      <ServiceData label={i18n.t('editService.serviceAccountable')} value={selectedService.inspectedBy.name || '-'}/>
      <ServiceData label={i18n.t('editService.doneInDelay')} value={selectedService.doneInDelay}/>
      <ServiceData label={i18n.t('editService.endOfMonthExpiration')} value={selectedService.equipmentService.expirationOnLastDayOfMonth}/>
      <ServiceData label={i18n.t('editService.actionsNumber')} value={totalActionsText} />
      <ServiceData label={i18n.t('editService.mandatoryActions')} value={selectedService.mandatoryActionsForNotComplyQuestions} showInfo/>
      <ServiceData label={i18n.t('editService.source')} value={serviceSources[selectedService.source as Source] as any} />
    </Paper>
  )
}

const sxs = {
  serviceDetailsCardContainer: {
    display: 'flex',
    flexDirection: 'column',
    // height: '540px',
    width: '50%',
    gap: 3,
    padding: '24px 24px',
    borderRadius: '12px',
    border: '1px solid var(--primary-hover, #ECEEF3)',
    boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.20), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
  },
  datePicker: {
    maxWidth: '150px'
  },
  label: {
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '12px',
    letterSpacing: '0.15px',
  },
  value: {
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
}

export default ServiceDetailsCard;
